/**
 * Resize + encode images before driver upload (weak cellular).
 * Prefers WebP (~25–40% smaller than JPEG at similar visual quality); falls back to JPEG.
 */

let _canvasSupportsWebp;
function canvasSupportsWebp() {
  if (_canvasSupportsWebp !== undefined) return _canvasSupportsWebp;
  try {
    const c = document.createElement("canvas");
    c.width = 1;
    c.height = 1;
    _canvasSupportsWebp = c.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    _canvasSupportsWebp = false;
  }
  return _canvasSupportsWebp;
}

/**
 * @param {File|Blob} file
 * @param {object} [options]
 * @param {number} [options.maxEdge=512] — longest side cap (pixels); driver proofs only need readability
 * @param {number} [options.webpQuality=0.62] — 0–1; smaller files on weak mobile data
 * @param {number} [options.jpegQuality=0.62] — used when WebP is unavailable
 * @returns {Promise<{ blob: Blob, extension: string }>}
 */
export async function compressImageForUpload(file, options = {}) {
  const maxEdge = options.maxEdge ?? 512;
  const webpQuality = options.webpQuality ?? 0.62;
  const jpegQuality = options.jpegQuality ?? 0.62;

  if (!file || !file.type || !file.type.startsWith("image/")) {
    const ext =
      (typeof file?.name === "string" && file.name.includes(".") && file.name.split(".").pop()) || "jpg";
    return { blob: file, extension: ext.slice(0, 8).toLowerCase() };
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { blob: file, extension: "jpg" };
  }

  try {
    let w = bitmap.width;
    let h = bitmap.height;
    if (Math.max(w, h) > maxEdge) {
      const r = maxEdge / Math.max(w, h);
      w = Math.round(w * r);
      h = Math.round(h * r);
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);

    const tryWebp = canvasSupportsWebp();
    if (tryWebp) {
      const webpBlob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("WebP encode failed"))),
          "image/webp",
          webpQuality
        );
      });
      return { blob: webpBlob, extension: "webp" };
    }

    const jpegBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("JPEG encode failed"))),
        "image/jpeg",
        jpegQuality
      );
    });
    return { blob: jpegBlob, extension: "jpg" };
  } catch {
    return { blob: file, extension: "jpg" };
  } finally {
    try {
      bitmap.close();
    } catch {
      /* ignore */
    }
  }
}

async function parseUploadError(res) {
  try {
    const j = await res.json();
    if (typeof j?.detail === "string") return j.detail;
    if (Array.isArray(j?.detail)) return j.detail.map((d) => d.msg || d).join("; ");
    return JSON.stringify(j);
  } catch {
    return res.statusText || "Upload failed";
  }
}

/**
 * POST FormData with retries (timeouts + backoff). Good for flaky mobile networks.
 */
export async function uploadFormWithRetry(url, formData, options = {}) {
  const retries = options.retries ?? 3;
  const timeoutMs = options.timeoutMs ?? 55000;
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch(url, { method: "POST", body: formData, signal: ctrl.signal });
      clearTimeout(timer);
      if (r.ok) return r;
      const msg = await parseUploadError(r);
      lastErr = new Error(msg);
      if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) {
        throw lastErr;
      }
    } catch (e) {
      clearTimeout(timer);
      lastErr = e;
    }
    if (attempt < retries - 1) {
      await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}
